// @flow
// $FlowFixMe - Cannot import useState because there is no useState export in react.
import React, { useState } from 'react';
import styled from 'styled-components';
import { useForm, FormProvider } from 'react-hook-form';
import { Button } from 'bench-internal-theme';
import { colors } from 'bench-style-constants';
import type { LedgerAccount } from '../types';
import getLedgerRole from './getLedgerRole';
import FunctionalDatesSidebarSection from './FunctionalDatesSidebarSection';
import createDateString from '../createDateString';
import FunctionalDataCollectionSidebarSection from './FunctionalDataCollectionSidebarSection';
import FunctionalAccountDetailsSection from './FunctionalAccountDetailsSection';
import LedgerAccountDetailsSidebarSection from './LedgerAccountDetailsSidebarSection';
import { categoryOptions, getCurrencyOptions } from './formOptions';
import useCurrency from './useCurrency';
// $FlowFixMe - TS files
import TargetLedgerMappingSidebarSection from './TargetLedgerMappingSidebarSection';

const LedgerAccountSidebarFormLayout = styled.form`
  bottom: 0;
  display: grid;
  grid-template-rows: 1fr min-content;
  left: 0;
  position: absolute;
  right: 0;
  top: 0;
`;

const FormFields = styled.div`
  overflow: auto;
`;

const FormActions = styled.div`
  background-color: ${colors.pearl};
  column-gap: 16px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  padding: 8px 24px;
  row-gap: 8px;

  #clear-form-button {
    grid-column: span 2;
  }
`;

type LedgerAccountSidebarFormProps = {
  closeSidebar: () => void,
  ledgerAccount: ?LedgerAccount,
  onSubmit: (payload: any) => any,
  isSubmitting: boolean,
};

const LedgerAccountSidebarForm = ({
  closeSidebar,
  ledgerAccount,
  onSubmit,
  isSubmitting,
}: LedgerAccountSidebarFormProps) => {
  const methods = useForm();
  const { handleSubmit, reset } = methods;
  const [isTemporary, setIsTemporary] = useState(ledgerAccount ? ledgerAccount.isTemporary : false);
  const [isFunctional, setIsFunctional] = useState(ledgerAccount ? ledgerAccount.role : false);
  const [autoPostEnabled, setAutoPostEnabled] = useState(
    ledgerAccount ? ledgerAccount.autoPostEnabled : true,
  );

  const categoryId = methods.watch('categoryId');

  const createPayload = fieldValues => ({
    name: fieldValues?.name || null,
    details: fieldValues?.details || null,
    currencyId: fieldValues?.currencyId?.value || null,
    categoryId: fieldValues?.categoryId?.value || null,
    displayGroupId: fieldValues?.displayGroupId?.value || null,
    isTemporary,
    functionalInput: isFunctional
      ? {
          autoPostEnabled: isFunctional ? autoPostEnabled : null,
          thirdPartyLoginAvailable:
            isFunctional && fieldValues?.thirdPartyLoginAvailable
              ? fieldValues?.thirdPartyLoginAvailable?.value
              : null,
          financialInstitutionProductId:
            isFunctional && fieldValues?.productType ? fieldValues?.productType?.value : null,
          fdsAccountId:
            isFunctional && fieldValues?.fdsAccountId ? fieldValues?.fdsAccountId?.value : null,
          role: isFunctional ? getLedgerRole(fieldValues) : null,
          optimizationStatus:
            isFunctional && fieldValues?.optimizationStatus
              ? fieldValues?.optimizationStatus?.value
              : null,
          collectionMethod:
            isFunctional && fieldValues?.collectionMethod
              ? fieldValues?.collectionMethod?.value
              : null,
          statementDayOfMonth:
            isFunctional && fieldValues?.statementDayOfMonth
              ? fieldValues?.statementDayOfMonth?.value
              : null,
          dateActive:
            isFunctional && fieldValues?.dateActiveYear && fieldValues?.dateActiveMonth
              ? createDateString(
                  fieldValues?.dateActiveYear?.value,
                  fieldValues?.dateActiveMonth?.value,
                )
              : null,
          dateInactive:
            isFunctional && fieldValues?.dateInactiveYear && fieldValues?.dateInactiveMonth
              ? createDateString(
                  fieldValues?.dateInactiveYear?.value,
                  fieldValues?.dateInactiveMonth?.value,
                )
              : null,
          volume: isFunctional && fieldValues?.volume ? fieldValues?.volume?.value : null,
          isAutoGenerated: ledgerAccount?.isAutoGenerated || false,
          accountLastFourDigits:
            isFunctional && fieldValues?.accountLastDigits
              ? // $FlowFixMe - Flow does not yet support method or property calls in optional chains
                fieldValues?.accountLastDigits?.toString()
              : null,
          targetLedgerMappings:
            fieldValues?.pBDrawLedger && fieldValues?.pBContributionLedger
              ? [
                  {
                    targetLedgerAccountId: fieldValues?.pBDrawLedger?.value,
                    entryType: 'DEBIT',
                  },
                  {
                    targetLedgerAccountId: fieldValues?.pBContributionLedger?.value,
                    entryType: 'CREDIT',
                  },
                ]
              : null,
        }
      : null,
  });

  const { currencies, clientCurrencyIdDefault } = useCurrency();
  const currencyOptions = getCurrencyOptions(currencies);

  const handleFormReset = () => {
    setIsFunctional(false);
    setIsTemporary(false);

    reset({
      name: '',
      details: '',
      categoryId: categoryOptions(false).find(categoryOption => categoryOption.value === '1'),
      currencyId: currencyOptions.find(
        currencyOption =>
          clientCurrencyIdDefault && currencyOption.value === clientCurrencyIdDefault,
      ),
      displayGroupId: null,
    });
  };

  const isEditing = ledgerAccount?.id;

  return (
    <FormProvider {...methods}>
      <LedgerAccountSidebarFormLayout
        onSubmit={handleSubmit(fieldValues =>
          onSubmit(createPayload(fieldValues)).then(() => {
            if (isEditing) {
              closeSidebar();
            }
          }),
        )}
      >
        <FormFields>
          <LedgerAccountDetailsSidebarSection
            isFunctional={isFunctional}
            isSubmitting={isSubmitting}
            isTemporary={isTemporary}
            ledgerAccount={ledgerAccount}
            setIsFunctional={setIsFunctional}
            setIsTemporary={setIsTemporary}
          />
          {isFunctional && (
            <>
              <FunctionalAccountDetailsSection
                isSubmitting={isSubmitting}
                isTemporary={isTemporary}
                ledgerAccount={ledgerAccount}
              />
              <FunctionalDatesSidebarSection
                ledgerAccount={ledgerAccount}
                isSubmitting={isSubmitting}
              />
              <FunctionalDataCollectionSidebarSection
                autoPostEnabled={autoPostEnabled}
                setAutoPostEnabled={setAutoPostEnabled}
                isTemporary={isTemporary}
                isSubmitting={isSubmitting}
                ledgerAccount={ledgerAccount}
              />
              {categoryId?.value === '9' ? (
                <TargetLedgerMappingSidebarSection
                  ledgerAccount={ledgerAccount}
                  isSubmitting={isSubmitting}
                />
              ) : null}
            </>
          )}
        </FormFields>
        <FormActions>
          <Button
            disabled={isSubmitting}
            size="large"
            styleType="ghost"
            role="button"
            aria-label="Cancel Ledger"
            onClick={closeSidebar}
          >
            Cancel
          </Button>
          <Button
            disabled={isSubmitting}
            size="large"
            styleType="primary"
            role="button"
            type="submit"
            aria-label="Save"
          >
            Save
          </Button>
          {!isEditing && (
            <Button
              disabled={isSubmitting}
              size="large"
              aria-label="Clear Form"
              styleType="primary"
              id="clear-form-button"
              onClick={event => {
                event.preventDefault();
                handleFormReset();
              }}
            >
              Clear Form
            </Button>
          )}
        </FormActions>
      </LedgerAccountSidebarFormLayout>
    </FormProvider>
  );
};
LedgerAccountSidebarForm.defaultProps = {
  ledgerAccount: undefined,
};

export default LedgerAccountSidebarForm;
